import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import Footer from '../../components/Footer';
import { 
  Car, 
  Clock, 
  Wallet, 
  Star, 
  Headphones,
  DollarSign,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

export default function DriversPage() {
  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_mercamove/artifacts/ch8knfdf_Move_Logo_wHT.png" 
                alt="Mercapoli Move"
                className="h-8"
              />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-slate-700 hover:text-[#00BFA6]">
                  Iniciar Sesión
                </Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary px-6 py-2 text-sm">
                  Registrarse
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-[#00BFA6]/10 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00BFA6]/10 rounded-full mb-6">
                <Car className="w-5 h-5 text-[#00BFA6]" />
                <span className="text-[#00BFA6] font-medium text-sm">Conductores</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 font-['Outfit'] leading-tight">
                Conduce con
                <span className="text-[#00BFA6]"> Mercapoli Move</span>
              </h1>
              <p className="text-lg text-slate-600 mt-6 leading-relaxed">
                Genera ingresos conduciendo en tu propio horario.
              </p>
              <p className="text-slate-600 mt-4">
                Mercapoli Move ofrece a los conductores una plataforma transparente, pagos 
                digitales rápidos y acceso a múltiples servicios desde una sola app.
              </p>
              <Link to="/register">
                <Button className="btn-primary mt-8 h-14 px-8 text-lg">
                  Registrarse como conductor
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=600&h=400&fit=crop" 
                alt="Driver"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 font-['Outfit'] text-center mb-12">
            Beneficios para Conductores
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-slate-50 p-6 rounded-2xl">
              <div className="w-14 h-14 bg-[#00BFA6]/10 rounded-xl flex items-center justify-center mb-4">
                <Clock className="w-7 h-7 text-[#00BFA6]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Horarios flexibles</h3>
              <p className="text-slate-600">Tú decides cuándo y cuánto trabajar. Sin jefes ni horarios fijos.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl">
              <div className="w-14 h-14 bg-[#FFD166]/10 rounded-xl flex items-center justify-center mb-4">
                <DollarSign className="w-7 h-7 text-[#FFD166]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Ganancias claras</h3>
              <p className="text-slate-600">Conoce exactamente cuánto ganas por cada viaje sin sorpresas.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl">
              <div className="w-14 h-14 bg-[#00BFA6]/10 rounded-xl flex items-center justify-center mb-4">
                <Wallet className="w-7 h-7 text-[#00BFA6]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Pagos con Digi Wallet</h3>
              <p className="text-slate-600">Recibe tus ganancias directamente en tu billetera digital.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl">
              <div className="w-14 h-14 bg-[#FFD166]/10 rounded-xl flex items-center justify-center mb-4">
                <Star className="w-7 h-7 text-[#FFD166]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Sistema de calificaciones justo</h3>
              <p className="text-slate-600">Tu reputación te abre puertas a más y mejores viajes.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl">
              <div className="w-14 h-14 bg-[#00887A]/10 rounded-xl flex items-center justify-center mb-4">
                <Headphones className="w-7 h-7 text-[#00887A]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Soporte continuo</h3>
              <p className="text-slate-600">Equipo de soporte disponible 24/7 para ayudarte.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-20 px-4 bg-[#F5F7FA]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 font-['Outfit'] text-center mb-12">
            Requisitos para ser conductor
          </h2>
          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-[#00BFA6] mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">Ser mayor de 21 años</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-[#00BFA6] mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">Licencia de conducir vigente</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-[#00BFA6] mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">Vehículo en buen estado (no mayor a 10 años)</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-[#00BFA6] mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">Seguro de auto vigente</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-[#00BFA6] mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">Pasar verificación de antecedentes</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-[#00BFA6] mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">Smartphone con conexión a internet</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-[#00BFA6]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-4">
            Comienza a generar ingresos hoy
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Únete a miles de conductores que ya forman parte de Mercapoli Move.
          </p>
          <Link to="/register">
            <Button className="bg-white text-[#00BFA6] hover:bg-white/90 h-14 px-8 text-lg rounded-full">
              Registrarme como Conductor
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
